import { useApiResponse } from '@/composables/useApiResponse';
import { useLaunchToast } from '@/composables/useToast';
import {
    DEFAULT_PAGE_SIZE,
    MIN_AUTOCOMPLETE_DIGITS,
    QUERY_SPECIAL_CASE_AUTOCOMPLETE,
    QUERY_SPECIAL_CASE_LIST,
    QUERY_SPECIAL_CASE_PARTNERS,
} from '@/constants';
import type {
    PartnerDtoSearchResponse,
    SpecialCaseAddedNotification,
    SpecialCaseDto,
    SpecialCaseDtoPagedListResponse,
    SpecialCaseRemovedNotification,
} from '@/generated';
import useAuthStore from '@/stores/useAuthStore';
import useDialogStore from '@/stores/useDialogStore';
import useOrderStore from '@/stores/useOrderStore';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { type ComputedRef, type Ref, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { partnerApi } from './generatedApi';

const updateSpecialCaseListPagination = (data: SpecialCaseDtoPagedListResponse) => {
    const pageIndex = data.pageIndex ?? 0;
    const pageSize = data.pageSize ?? data.data?.length ?? DEFAULT_PAGE_SIZE;
    const totalCount = data.totalCount ?? data.data?.length ?? 0;
    const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;

    return {
        ...data,
        totalPages,
        hasPreviousPage: pageIndex > 0,
        hasNextPage: pageIndex + 1 < totalPages,
    };
};

const setPartnerSpecialCaseFlagInAutocompleteCache = (
    queryClient: QueryClient,
    partnerId: number,
    isSpecialCase: boolean
) => {
    queryClient.setQueriesData<PartnerDtoSearchResponse[]>(
        { queryKey: [QUERY_SPECIAL_CASE_AUTOCOMPLETE] },
        oldData => {
            if (!oldData?.length) {
                return oldData;
            }

            let hasChange = false;
            const updated = oldData.map(suggestion => {
                if (
                    suggestion.partner?.id !== partnerId ||
                    suggestion.isSpecialCase === isSpecialCase
                ) {
                    return suggestion;
                }

                hasChange = true;
                return {
                    ...suggestion,
                    isSpecialCase,
                };
            });

            return hasChange ? updated : oldData;
        }
    );
};

const findPartnerInAutocompleteCache = (queryClient: QueryClient, partnerId: number) => {
    const autocompleteQueries = queryClient.getQueriesData<PartnerDtoSearchResponse[]>({
        queryKey: [QUERY_SPECIAL_CASE_AUTOCOMPLETE],
    });

    for (const [, suggestions] of autocompleteQueries) {
        const foundPartner = suggestions?.find(s => s.partner?.id === partnerId)?.partner;
        if (foundPartner) {
            return foundPartner;
        }
    }

    return undefined;
};

const getNextData = (
    oldData: SpecialCaseDtoPagedListResponse | undefined,
    currentData: SpecialCaseDto[],
    specialCase: SpecialCaseDto,
    partnerId: number,
    hasExistingPartner: boolean
) => {
    const shouldPrependToFirstPage =
        (oldData?.pageIndex ?? 0) === 0
            ? [specialCase, ...currentData].slice(
                  0,
                  oldData?.pageSize ?? currentData.length ?? DEFAULT_PAGE_SIZE
              )
            : currentData;

    return hasExistingPartner
        ? currentData.map(item =>
              item.partner?.id === partnerId
                  ? {
                        partner: specialCase.partner ?? item.partner,
                        caseConstructor: specialCase.caseConstructor ?? item.caseConstructor,
                        isIndividual: specialCase.isIndividual ?? item.isIndividual,
                    }
                  : item
          )
        : shouldPrependToFirstPage;
};

const mergePartnerIntoSpecialCaseList = (
    oldData: SpecialCaseDtoPagedListResponse | undefined,
    specialCase: SpecialCaseDto
) => {
    if (!oldData) {
        return oldData;
    }

    const partnerId = specialCase.partner?.id;
    if (typeof partnerId !== 'number') {
        return oldData;
    }

    const currentData = oldData.data ?? [];
    const hasExistingPartner = currentData.some(item => item.partner?.id === partnerId);

    const nextData = getNextData(oldData, currentData, specialCase, partnerId, hasExistingPartner);
    const nextTotalCount =
        (oldData.totalCount ?? currentData.length) + (hasExistingPartner ? 0 : 1);

    return updateSpecialCaseListPagination({
        ...oldData,
        data: nextData,
        totalCount: nextTotalCount,
    });
};

export const addSpecialCasePartnerToCache = (
    queryClient: QueryClient,
    specialCase: SpecialCaseDto
) => {
    const partnerId = specialCase.partner?.id;
    if (typeof partnerId !== 'number') {
        return;
    }

    queryClient.setQueriesData<SpecialCaseDtoPagedListResponse>(
        { queryKey: [QUERY_SPECIAL_CASE_LIST] },
        oldData => mergePartnerIntoSpecialCaseList(oldData, specialCase)
    );

    setPartnerSpecialCaseFlagInAutocompleteCache(queryClient, partnerId, true);
};

export const removeSpecialCasePartnerFromCache = (queryClient: QueryClient, partnerId: number) => {
    queryClient.setQueriesData<SpecialCaseDtoPagedListResponse>(
        { queryKey: [QUERY_SPECIAL_CASE_LIST] },
        oldData => {
            if (!oldData?.data?.length) {
                return oldData;
            }

            const filtered = oldData.data.filter(item => item.partner?.id !== partnerId);
            if (filtered.length === oldData.data.length) {
                return oldData;
            }

            const nextTotalCount = Math.max(
                0,
                (oldData.totalCount ?? oldData.data.length) -
                    (oldData.data.length - filtered.length)
            );

            return updateSpecialCaseListPagination({
                ...oldData,
                data: filtered,
                totalCount: nextTotalCount,
            });
        }
    );

    setPartnerSpecialCaseFlagInAutocompleteCache(queryClient, partnerId, false);
};

export const applySpecialCaseNotificationToCache = (
    queryClient: QueryClient,
    notification: unknown
) => {
    if (!notification || typeof notification !== 'object') {
        return;
    }

    const addedNotification = notification as SpecialCaseAddedNotification;
    if (addedNotification.specialCase?.partner?.id) {
        addSpecialCasePartnerToCache(queryClient, addedNotification.specialCase);
        return;
    }

    const removedNotification = notification as SpecialCaseRemovedNotification;
    if (typeof removedNotification.partnerId === 'number') {
        removeSpecialCasePartnerFromCache(queryClient, removedNotification.partnerId);
    }
};

export const useGetSpecialCasePartnerList = (
    pageIndex?: Ref<number> | ComputedRef<number>,
    pageSize?: Ref<number> | ComputedRef<number>,
    queryEnabled: Ref<boolean> | ComputedRef<boolean> = ref(true)
) => {
    const { isAuthenticated } = storeToRefs(useAuthStore());
    const { hasSpecialCasesOrders } = storeToRefs(useOrderStore());
    const apiResponseCode = useApiResponse();

    return useQuery({
        queryKey: [QUERY_SPECIAL_CASE_LIST, pageIndex, pageSize],
        queryFn: async () => {
            try {
                const response = await partnerApi.apiV1PartnerSpecialcasesPost({
                    pagination: {
                        pageIndex: pageIndex?.value ?? 0,
                        pageSize: pageSize?.value ?? DEFAULT_PAGE_SIZE,
                    },
                });

                if (apiResponseCode(response.status).ok) {
                    hasSpecialCasesOrders.value = !!response.data?.totalCount;
                    return response.data;
                } else {
                    throw new Error(`Could not load special case partners. ${response.statusText}`);
                }
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        enabled: computed(() => isAuthenticated.value && queryEnabled?.value),
        staleTime: 0,
    });
};

export const useGetAutoCompletePartnerList = (partnerNumber: Ref<string>) => {
    const { isAuthenticated } = storeToRefs(useAuthStore());
    const { hasSpecialCasesOrders } = storeToRefs(useOrderStore());
    const apiResponseCode = useApiResponse();
    const hasMinDigits = computed(() => {
        const digitCount = (partnerNumber.value.match(/\d/g) || []).length;
        return digitCount >= MIN_AUTOCOMPLETE_DIGITS;
    });

    return useQuery({
        queryKey: [QUERY_SPECIAL_CASE_AUTOCOMPLETE, partnerNumber],
        queryFn: async ({ queryKey }) => {
            const [, currentPartnerNumber = ''] = queryKey as [string, string?];
            try {
                const response =
                    await partnerApi.apiV1PartnerGemasPartnerIdGet(currentPartnerNumber);

                if (apiResponseCode(response.status).ok) {
                    hasSpecialCasesOrders.value = !!response.data?.length;
                    return response.data;
                } else {
                    throw new Error(
                        `Could not load auto complete partners. ${response.statusText}`
                    );
                }
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        enabled: computed(() => isAuthenticated.value && hasMinDigits.value),
    });
};

export const useAddSpecialCasePartner = () => {
    const { isAuthenticated } = storeToRefs(useAuthStore());
    const queryClient = useQueryClient();
    const toast = useLaunchToast();
    const { t } = useI18n();
    const { showConfirmationDialog } = storeToRefs(useDialogStore());

    return useMutation({
        mutationKey: [QUERY_SPECIAL_CASE_PARTNERS, 'add'],
        mutationFn: async (partnerId: number) => {
            try {
                if (!isAuthenticated.value) {
                    throw new Error('User is not authenticated', { cause: 401 });
                }
                await partnerApi.apiV1PartnerPost(partnerId);
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        onSuccess: (_, partnerId) => {
            const partner = findPartnerInAutocompleteCache(queryClient, partnerId) ?? {
                id: partnerId,
            };
            addSpecialCasePartnerToCache(queryClient, { partner });
        },
        onError: () => {
            toast('error', t('page.settingsSpecialCases.errors.addPartner.message'));
        },
        onSettled: () => {
            showConfirmationDialog.value = 0;
        },
    });
};

export const useDeleteSpecialCasePartner = () => {
    const { isAuthenticated } = storeToRefs(useAuthStore());
    const queryClient = useQueryClient();
    const toast = useLaunchToast();
    const { t } = useI18n();
    const { showConfirmationDialog } = storeToRefs(useDialogStore());

    return useMutation({
        mutationKey: [QUERY_SPECIAL_CASE_PARTNERS, 'delete'],
        mutationFn: async (partnerId: number) => {
            try {
                if (!isAuthenticated.value) {
                    throw new Error('User is not authenticated', { cause: 401 });
                }
                await partnerApi.apiV1PartnerPartnerIdDelete(partnerId);
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        onSuccess: (_, partnerId) => {
            removeSpecialCasePartnerFromCache(queryClient, partnerId);
        },
        onError: () => {
            toast('error', t('page.settingsSpecialCases.errors.deletePartner.message'));
        },
        onSettled: () => {
            showConfirmationDialog.value = 0;
        },
    });
};
